import { SecShape, type SecondaryDef } from '../../sim/secondary';

/** S10 — RAYON CONTINU. La seule arme sans temps de vol.
 *
 *  ── Le besoin ────────────────────────────────────────────────────────────
 *  Chaque arme du catalogue a un délai entre le clic et l'impact : 0,3 s pour
 *  la Lance à bout portant, 0,5 s pour les Flanqueurs sur un Rôdeur qui
 *  traverse, près d'une seconde pour un Traqueur. C'est invisible sur une cible
 *  lente et rédhibitoire sur tout le reste : un Dard pique à 720 u/s, soit
 *  360 u pendant que le projectile monte, et il n'est plus là. Les ouvertures
 *  de boss durent parfois moins que le temps de vol qui sépare le joueur de la
 *  zone à frapper. Il n'existait aucune façon de toucher MAINTENANT.
 *
 *  Le rayon est accroché au vaisseau : il touche à l'instant où on le pointe et
 *  continue de toucher tant qu'on le tient. Ce qu'il demande n'est ni de viser
 *  ni d'anticiper, mais de SUIVRE — pendant 1,4 s, le déplacement du joueur est
 *  sa conduite de tir. C'est la seule arme dont on pilote les dégâts au lieu de
 *  les déclencher.
 *
 *  ── Pourquoi ce n'est pas un Faisceau bref plus long ─────────────────────
 *  Les deux se ressemblent à l'écran et sont opposés à la main, comme la
 *  Traînée et le Faisceau le sont déjà — mais ici l'écart est de moteur et non
 *  de données :
 *
 *    · le Faisceau bref tire dix-huit volées de projectiles très rapides. Ils
 *      PARTENT. Bouger pendant la fenêtre les envoie ailleurs, donc l'arme
 *      punit le déplacement et exige de tenir sa ligne ;
 *    · le rayon ne tire rien. Ses segments sont replacés sur le vaisseau à
 *      chaque tick, et la collision balayée relie leur position d'avant-tick à
 *      la nouvelle : un balayage latéral touche donc tout ce qu'il traverse.
 *      L'arme récompense le déplacement, et c'est le déplacement lui-même qui
 *      distribue les dégâts.
 *
 *  ── Pourquoi une cadence d'impulsion, et pourquoi elle est dans le moteur ─
 *  Un segment ancré chevauche sa cible pendant des dizaines de ticks. Sans
 *  cadence il infligerait ses dégâts 120 fois par seconde — une arme mille fois
 *  au-dessus de la primaire — et l'anneau d'événements saturerait sur une seule
 *  cible, faisant disparaître les explosions du reste de la partie. Les 0,1 s
 *  vivent dans `BEAM_PULSE` parce qu'elles définissent ce QU'EST un rayon ; les
 *  exposer au contenu laisserait écrire un rayon à 0,4 s, qui serait une salve.
 *
 *  ── Pourquoi SEPT segments larges et non douze fins ──────────────────────
 *  C'est l'équilibrage qui a dicté la géométrie, et le détour vaut d'être noté
 *  parce qu'il est contre-intuitif. La première version avait douze segments de
 *  26 u : élégante à l'écran, elle délivrait 163 DPS sur une grille dense pour
 *  70 annoncés, parce que douze segments frappent douze corps différents à
 *  chaque impulsion. Un projectile ordinaire ne peut pas dépasser son nominal —
 *  il meurt sur sa première cible — un rayon, si.
 *
 *  Une fois les segments comptés honnêtement, rester puissant à nominal borné
 *  impose d'en avoir PEU, donc de GROS. Sept segments de 38 u : le rayon est
 *  plus large, plus lisible, et sa mesure sur banc coïncide enfin avec son
 *  annonce. La contrainte a produit la bonne forme.
 *
 *  ── L'équilibrage ────────────────────────────────────────────────────────
 *  110 DPS nominaux (55 % de la primaire) : sept segments × quatorze impulsions
 *  × 9 dégâts, sur 8 s de recharge. Sur une cible unique, trois segments la
 *  chevauchent : 378 dégâts livrés en 1,4 s, soit 270 DPS instantanés le temps
 *  qu'on la tient.
 *
 *  ── Le prix, et il est réel ──────────────────────────────────────────────
 *  426 u de portée. Depuis le bas du terrain, le rayon n'atteint pas la ligne
 *  où les ennemis se postent : il faut MONTER. C'est la seule arme du catalogue
 *  qui demande d'avancer verticalement, là où l'Orage demande de s'approcher
 *  latéralement d'un groupe — deux façons de payer, deux dangers différents. */
const rayon: SecondaryDef = {
  id: 'rayon-continu',
  name: 'Rayon continu',
  shape: SecShape.Rayon,
  // 8 s, le maximum autorisé, pour 1,4 s d'émission : l'arme est éteinte 82 %
  // du temps. Un rayon disponible plus souvent cesserait d'être une décision
  // pour devenir une seconde arme principale que l'on rallume.
  cooldown: 8,
  shots: 1,
  shotEvery: 0,
  // Segments de la colonne. Ce n'est pas un nombre de projectiles au sens
  // habituel : c'est la LONGUEUR du rayon — 396 u au-dessus de la bouche —
  // exprimée dans la seule unité que le pool sache manipuler. ⚠️ Chaque segment
  // ajouté est une cible de plus par impulsion, donc du DPS réel : allonger le
  // rayon n'est jamais gratuit, quoi qu'en dise l'intuition.
  count: 7,
  // Pas de la colonne. ⚠️ Il doit rester sous le diamètre d'un segment (76 u),
  // sinon le rayon devient un pointillé : un ennemi passe entre deux segments et
  // le joueur voit un trait continu qui ne touche pas.
  spacing: 66,
  spreadDeg: 0,
  // Aucune. Un rayon ancré ne se déplace pas de lui-même — c'est sa définition,
  // et c'est ce qui supprime son temps de vol.
  speed: 0,
  // Large : 76 u de diamètre. Un rayon fin serait une arme de précision, ce qui
  // contredirait tout le reste — on le pointe en pilotant, pas en visant.
  radius: 38,
  damage: 9,
  // 1,4 s, soit quatorze impulsions. Au-delà de ~1,5 s, tenir une cible dans un
  // rideau devient une punition plutôt qu'une performance ; en deçà d'une
  // seconde, le balayage n'a pas le temps d'exister et l'arme redevient un
  // burst.
  duration: 1.4,
  text: '7 segments × 9 dégâts toutes les 0,1 s pendant 1,4 s — 8 s',
};

export default rayon;
